import React, {
  useCallback,
  useEffect,
  useState
} from 'react';
import { useParams } from 'react-router-dom';

import {
  getCountryName,
  getFromSession,
  setFromSession,
  removeFromSession,
  isValidCountryCode
} from 'wtb-utils';
import { Form, Icon, Typography } from 'wtb-components';
import { useLocaleContext, useTransactionContext } from 'context';
import { APP_STEPS, ANALYTICS_EVENTS } from 'constants.js';
import { useSendAnalyticsEvent } from 'hooks';
import uiIcons from 'uiIcons';
import {
  setFieldFunctions,
  getFirstNameField,
  getLastNameField,
  getNationalIdField,
  getEmailField,
  getPhoneField,
  getStreetField,
  getStreetAdditionalField,
  getZipCodeField,
  getCityField,
  getCountryField
} from 'utils/fields';
import styles from './PersonalInfoForm.module.css';

const SectionTitle = ({ iconName, title }) => {
  return (
    <div className={styles.sectionTitle}>
      <Icon name={iconName} />
      <Typography variant="subtitle1">{title}</Typography>
    </div>
  );
};

const PersonalInfoForm = (props) => {

  const {
    info,
    setData,
    fields: _fields,
    setHasError,
    SubmitButton,
    SubmitButtonProps = {}
  } = props;

  const { translate, baseLocale } = useLocaleContext();
  const {
    campaign: {
      country: campaignCountry
    }
  } = useTransactionContext();

  const sendAnalyticsEvent = useSendAnalyticsEvent();

  const { step } = useParams();

  const [ hasStreetAdditional, setHasStreetAdditional ] = useState(false);
  const [ fields, setFields ] = useState([]);

  const initForm = useCallback(() => {
    setFieldFunctions({
      classes: styles,
      translate
    });

    const savedData = JSON.parse(getFromSession('currentForm') || '{}');

    const _data = {
      ...savedData,
      ...info
    };

    if (!_data.country) {
      _data.country = getCountryName(campaignCountry, baseLocale);
    } else if (isValidCountryCode(_data.country)) {
      _data.country = getCountryName(_data.country, baseLocale);
    } else if (!_data.country) {
      Logger.error(`Getting country name in ${_data.language} didn't work. Defaulting to English.`);
      getCountryName(_data.address.country, 'en');
    }

    const formFields = [];
    const _aboutFields = [];
    const _contactFields = [];
    const _addressFields = [];

    let _hasAboutFields = false;
    let _hasContactFields = false;
    let _hasAddressFields = false;

    if (_fields.name) {
      _aboutFields.push(getFirstNameField(_data.firstName));
      _aboutFields.push(getLastNameField(_data.lastName));
      _hasAboutFields = true;
    }
    if (_fields.nationalId) {
      _aboutFields.push(getNationalIdField(_data.nationalId, campaignCountry));
      _hasAboutFields = true;
    }

    if (_fields.email) {
      _contactFields.push(getEmailField(_data.email));
      _hasContactFields = true;
    }
    if (_fields.phone) {
      _contactFields.push(getPhoneField(_data.phone, campaignCountry));
      _hasContactFields = true;
    }

    if (_fields.address) {
      _addressFields.push(getStreetField(
        _data.street,
        hasStreetAdditional,
        () => setHasStreetAdditional(true),
        campaignCountry
      ));

      if (hasStreetAdditional) {
        _addressFields.push(getStreetAdditionalField(_data.streetAdditional));
      }
      // _addressFields.push(getStreetAdditionalField(_data.streetAdditional));
      _addressFields.push(getZipCodeField(_data.zipCode, _data.country || getCountryName(campaignCountry, baseLocale)));
      _addressFields.push(getCityField(_data.city));
      _addressFields.push(getCountryField(_data.country));

      _hasAddressFields = true;
    }

    if (_hasAboutFields) {
      formFields.push({
        section: 'about',
        title: <SectionTitle iconName={uiIcons.person} title={translate('customerData.aboutYou')} />,
        wrapperClassName: styles.sectionWrapper,
        fields: _aboutFields
      });
    }
    if (_hasContactFields) {
      formFields.push({
        section: 'contact',
        title: <SectionTitle iconName={uiIcons.mail} title={translate('customerData.contactInfo')} />,
        wrapperClassName: styles.sectionWrapper,
        fields: _contactFields
      });
    }
    if (_hasAddressFields) {
      formFields.push({
        section: 'address',
        title: <SectionTitle iconName={uiIcons.home} title={translate('customerData.shippingAddress')} />,
        wrapperClassName: styles.sectionWrapper,
        fields: _addressFields
      });
    }

    if (!_hasAboutFields && !_hasContactFields && !_hasAddressFields && setHasError) {
      setHasError(false);
    }

    setFields(formFields);
  }, [
    _fields,
    campaignCountry,
    hasStreetAdditional,
    info,
    baseLocale,
    setHasError,
    translate
  ]);

  useEffect(() => {
    if (!_fields) return;
    initForm();
  }, [ _fields, initForm ]);

  const handleChange = (values) => {
    const savedData = JSON.parse(getFromSession('currentForm') || '{}');
    setFromSession('currentForm', JSON.stringify({ ...savedData, ...values }));
  };

  const handleSubmit = (values) => {
    removeFromSession('currentForm');
    setData({ ...info, ...values });
  };

  // Google analytics event to calculate the time spent in the personal information form
  useEffect(() => {
    if (step === APP_STEPS.PERSONAL_INFORMATION) {
      const startTime = performance.now();

      // Cleanup function to calculate and send the timing data when the component unmounts
      return () => {
        const endTime = performance.now();
        const elapsedTime = Math.round((endTime - startTime) / 1000);

        sendAnalyticsEvent(ANALYTICS_EVENTS.PERSONAL_INFO_TIMING, { time: elapsedTime });
      };
    }
    return () => {};
  }, [ step, sendAnalyticsEvent ]);

  return (
    <div className={styles.root}>
      <Typography
        className={styles.requiredFields}
        variant="caption"
        component="div"
      >
        {translate('customerData.requiredFields')}
      </Typography>

      <Form
        className={styles.form}
        fields={fields}
        SubmitButton={SubmitButton}
        SubmitButtonProps={SubmitButtonProps}
        onChange={handleChange}
        onSubmit={handleSubmit}
        name="personal-info-form"
        scrollableContainerId="scrollable-body"
        autoScrollOffset={-250}
      />
    </div>
  );
};

export default PersonalInfoForm;
